import { useCallback, useEffect, useState } from "react";
import type { CentroCustoApi, TipoCentroCusto } from "../types/centro-custo.types";
import { listarCentrosCusto } from "../services/centro-custo.service";

export type UseCentrosCustoOptions = {
  ativo?: boolean;
  tipo?: TipoCentroCusto;
  /** Cria centros de custo faltantes para eventos ativos antes de listar. */
  sincronizarEventosAtivos?: boolean;
  /** Exibe só centros de eventos publicados com data >= hoje. */
  somenteEventosAtivos?: boolean;
};

export type UseCentrosCustoResult = {
  data: CentroCustoApi[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
};

export function useCentrosCusto(
  options?: UseCentrosCustoOptions,
): UseCentrosCustoResult {
  const [data, setData] = useState<CentroCustoApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const ativo = options?.ativo ?? true;
  const tipo = options?.tipo;
  const sincronizarEventosAtivos = options?.sincronizarEventosAtivos ?? true;
  const somenteEventosAtivos = options?.somenteEventosAtivos ?? false;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: list, status } = await listarCentrosCusto({
          ativo,
          tipo,
          sincronizarEventosAtivos,
          somenteEventosAtivos,
        });
        if (cancelled) return;
        if (status === 403) {
          setError("Sem permissão para listar centros de custo.");
          setData([]);
          return;
        }
        if (status !== 200 || !list) {
          setError("Não foi possível carregar os centros de custo.");
          setData([]);
          return;
        }
        setData(list);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          e instanceof Error ? e.message : "Falha ao carregar centros de custo.";
        setError(msg);
        setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [ativo, tipo, sincronizarEventosAtivos, somenteEventosAtivos, tick]);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
  };
}
